import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Plus, X, ChatCircleDots, Phone, GlobeHemisphereWest, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const STAGES = [
  { id: "new", label: "New", color: "#002FA7" },
  { id: "contacted", label: "Contacted", color: "#EAB308" },
  { id: "booked", label: "Booked", color: "#06B6D4" },
  { id: "won", label: "Won", color: "#22C55E" },
  { id: "lost", label: "Lost", color: "#71717A" },
];

const SOURCE_ICONS = {
  manual: Plus,
  chat_widget: ChatCircleDots,
  missed_call: Phone,
  voice_agent: Phone,
  web_form: GlobeHemisphereWest,
};

export default function Leads() {
  const { activeBiz } = useOutletContext();
  const [byStage, setByStage] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", intent: "" });
  const [dragId, setDragId] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    if (!activeBiz) return;
    try {
      const { data } = await api.get(`/businesses/${activeBiz.id}/leads`);
      setByStage(data.by_stage);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [activeBiz]);

  if (!activeBiz) return <div className="p-12 text-zinc-500">Add a business first.</div>;

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/businesses/${activeBiz.id}/leads`, form);
      setForm({ name: "", email: "", phone: "", intent: "" });
      setShowAdd(false);
      reload();
      toast.success("Lead added");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const moveStage = async (lid, stage) => {
    try {
      await api.put(`/businesses/${activeBiz.id}/leads/${lid}/stage`, { stage });
      reload();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (lid) => {
    try { await api.delete(`/businesses/${activeBiz.id}/leads/${lid}`); reload(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const simulateMissed = async () => {
    setBusy(true);
    try {
      await api.post(`/missed-call/simulate`, { business_id: activeBiz.id, from_number: "+15555550199" });
      toast.success("Simulated missed call → SMS auto-sent");
      reload();
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  const simulateVoice = async () => {
    setBusy(true);
    try {
      await api.post(`/businesses/${activeBiz.id}/voice-calls/simulate`);
      toast.success("Simulated voice agent call → lead added");
      reload();
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-[1400px]">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="label-tiny mb-1">Leads</div>
          <h1 className="font-display text-4xl font-black tracking-tighter" data-testid="leads-title">Pipeline</h1>
          <p className="text-zinc-500 mt-1">Every inbound lead — chat widget, missed calls, voice agent, web forms — funnels here.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="btn-secondary !py-2 !text-[11px]" onClick={simulateMissed} disabled={busy} data-testid="leads-sim-missed">Simulate missed call</button>
          <button className="btn-secondary !py-2 !text-[11px]" onClick={simulateVoice} disabled={busy} data-testid="leads-sim-voice">Simulate voice call</button>
          <button className="btn-dark !py-2 !text-[11px]" onClick={() => setShowAdd(!showAdd)} data-testid="leads-add-toggle"><Plus size={12} weight="bold" /> Add lead</button>
        </div>
      </header>

      {showAdd && (
        <form onSubmit={add} className="bento p-4 grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="leads-add-form">
          <input className="input-field" placeholder="Name *" required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} />
          <input className="input-field" type="email" placeholder="Email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} />
          <input className="input-field" placeholder="Phone" value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} />
          <input className="input-field" placeholder="Intent" value={form.intent} onChange={(e)=>setForm({...form, intent: e.target.value})} />
          <button className="btn-primary justify-center" type="submit" data-testid="leads-add-submit">Add</button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="leads-kanban">
        {STAGES.map((s) => (
          <div
            key={s.id}
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={() => { if (dragId) { moveStage(dragId, s.id); setDragId(null); } }}
            className="bg-zinc-100 border border-zinc-200 min-h-[400px] flex flex-col"
            data-testid={`leads-col-${s.id}`}
          >
            <div className="p-3 border-b border-zinc-200 flex items-center justify-between" style={{ borderTop: `2px solid ${s.color}` }}>
              <span className="label-tiny" style={{ color: s.color }}>{s.label}</span>
              <span className="font-mono text-xs text-zinc-500">{(byStage[s.id] || []).length}</span>
            </div>
            <div className="flex-1 p-2 space-y-2 overflow-auto">
              {(byStage[s.id] || []).map((l) => {
                const Icon = SOURCE_ICONS[l.source] || Plus;
                return (
                  <div
                    key={l.id}
                    draggable
                    onDragStart={() => setDragId(l.id)}
                    onDragEnd={() => setDragId(null)}
                    className="bg-white p-3 border border-zinc-200 hover:border-zinc-400 cursor-grab active:cursor-grabbing"
                    data-testid={`lead-card-${l.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Icon size={12} className="text-zinc-500 shrink-0" />
                        <span className="font-semibold text-sm truncate">{l.name}</span>
                      </div>
                      <button onClick={() => remove(l.id)} className="text-zinc-400 hover:text-zinc-900 shrink-0"><X size={12} /></button>
                    </div>
                    {l.intent && <p className="text-xs text-zinc-600 mt-1 line-clamp-2">{l.intent}</p>}
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 font-mono text-[10px] text-zinc-500">
                      {l.phone && <span>{l.phone}</span>}
                      {l.email && <span className="truncate max-w-full">{l.email}</span>}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {STAGES.filter((x) => x.id !== l.stage).map((x) => (
                        <button key={x.id} onClick={() => moveStage(l.id, x.id)} className="text-[10px] px-1.5 py-0.5 bg-zinc-100 hover:bg-zinc-200 font-mono" data-testid={`move-${l.id}-${x.id}`}>→ {x.label}</button>
                      ))}
                    </div>
                  </div>
                );
              })}
              {(byStage[s.id] || []).length === 0 && (
                <div className="text-center text-zinc-400 text-xs font-mono py-8">empty</div>
              )}
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-zinc-500 font-mono">Tip: drag cards between columns or use the arrow buttons to advance the stage.</p>
    </div>
  );
}
